import { supabase } from "./supabase";

// ✅ SUPER ADMINS - Hardcodeado
const SUPER_ADMINS = [
  "matias@nokk.com.ar",
  "msonzinia@gmail.com",
  "tobias@nokk.com.ar",
];

// ✅ SUPER ADMIN CONSTRUCTORA ID - Hardcodeado
const SUPER_ADMIN_CONSTRUCTORA_ID = "d6b9495e-49a4-4c94-a435-ae0a10ea3f44";

/**
 * ✅ NUEVA FUNCIÓN: Verifica si un usuario es super admin
 * @param {Object} usuario - Usuario con email
 * @returns {boolean}
 */
export const esSuperAdmin = (usuario) => {
  return SUPER_ADMINS.includes(usuario?.email?.toLowerCase());
};

/**
 * ✅ NUEVA FUNCIÓN: Verifica acceso a vistas de sistema (super admin)
 * @param {Object} usuario - Usuario
 * @param {string} vista - ID de la vista (ej: 'system-demos')
 * @returns {boolean}
 */
export const puedeAccederAVistaAdmin = (usuario, vista) => {
  // Solo super admins pueden acceder a vistas de sistema
  if (vista.startsWith("system-")) {
    return esSuperAdmin(usuario);
  }
  return false;
};

/**
 * ✅ NUEVA FUNCIÓN: Verifica si un usuario puede acceder a la app
 * @param {Object} usuario - Usuario
 * @param {Object} constructora - Datos de la constructora
 * @returns {Object} - { puedeAcceder: boolean, motivo: string, estadoSuscripcion?: string }
 */
export const verificarAccesoApp = async (usuario, constructora) => {
  try {
    console.log('🔍 verificarAccesoApp iniciado');
    console.log('👤 Usuario:', usuario.email);
    console.log('🏢 Constructora:', constructora?.id);
    
    // 1. Super admins siempre tienen acceso
    if (esSuperAdmin(usuario)) {
      console.log('✅ Es super admin');
      return { puedeAcceder: true, motivo: "Super admin" };
    }
    
    // 2. Usuarios de super admin constructora siempre tienen acceso
    if (constructora?.id === SUPER_ADMIN_CONSTRUCTORA_ID) {
      console.log('✅ Es super admin constructora');
      return { puedeAcceder: true, motivo: "Super admin constructora" };
    }
    
    // 3. Para el resto, verificar estado de suscripción
    if (!constructora?.id) {
      return { puedeAcceder: false, motivo: "Constructora no encontrada" };
    }
    
    // Buscar suscripción activa para esta constructora
    console.log('🔍 Buscando suscripciones para constructora:', constructora.id);
    
    const { data: suscripciones, error } = await supabase
      .from('suscripciones')
      .select('id, estado, plan, fecha_inicio, fecha_cancelacion')
      .eq('constructora_id', constructora.id)
      .order('created_at', { ascending: false })
      .limit(1);
    
    console.log('📊 Suscripciones encontradas:', suscripciones);
    console.log('❌ Error si hay:', error);
    
    if (error) {
      console.error('Error verificando suscripción:', error);
      return { puedeAcceder: false, motivo: "Error verificando suscripción" };
    }
    
    if (!suscripciones || suscripciones.length === 0) {
      return { 
        puedeAcceder: false, 
        motivo: "Sin suscripción activa",
        estadoSuscripcion: "sin_suscripcion"
      };
    }
    
    const suscripcion = suscripciones[0];
    
    if (suscripcion.estado === 'authorized') {
      console.log('✅ Suscripción autorizada');
      return { 
        puedeAcceder: true, 
        motivo: "Suscripción autorizada",
        estadoSuscripcion: "authorized"
      };
    } else {
      console.log('❌ Suscripción NO autorizada, estado:', suscripcion.estado);
      return { 
        puedeAcceder: false, 
        motivo: `Suscripción ${suscripcion.estado}`,
        estadoSuscripcion: suscripcion.estado
      };
    }
    
  } catch (error) {
    console.error('Error en verificarAccesoApp:', error);
    return { puedeAcceder: false, motivo: "Error interno" };
  }
};

/**
 * Obtiene los permisos y datos del proyecto para un cliente
 * @param {Object} usuario - Usuario cliente logueado
 * @param {Object} constructora - Datos de la constructora
 * @returns {Object} - Permisos y datos del proyecto
 */
export const obtenerPermisosCliente = async (usuario, constructora) => {
  console.log("🔍 DEBUG - obtenerPermisosCliente iniciado");
  console.log("👤 Usuario:", usuario);
  console.log("🏢 Constructora:", constructora);

  if (!usuario || usuario.tipo_usuario !== "cliente") {
    console.log("❌ No es cliente o usuario inválido");
    return {
      esCliente: false,
      tipoProyecto: null,
      proyectoId: null,
      permisos: {}, // Asegurarse de que permisos siempre sea un objeto
      error: null,
    };
  }

  try {
    console.log("🔍 Buscando proyecto con email:", usuario.email);
    console.log("🔍 En constructora ID:", constructora.id);

    // Buscar el proyecto del cliente por email
    const { data: proyecto, error } = await supabase
      .from("proyectos")
      .select(
        "id, tipo_obra, codigo, apellido_cliente, obra_finalizada, email_cliente"
      )
      .eq("constructora_id", constructora.id)
      .eq("email_cliente", usuario.email)
      .single();

    console.log("📊 Resultado query proyecto:", { proyecto, error });

    if (error || !proyecto) {
      console.log("❌ No se encontró proyecto para email:", usuario.email);

      // Buscar todos los proyectos para debugging
      const { data: todosProyectos } = await supabase
        .from("proyectos")
        .select("email_cliente, codigo, apellido_cliente")
        .eq("constructora_id", constructora.id);

      console.log(
        "📋 Todos los emails en proyectos:",
        todosProyectos?.map((p) => p.email_cliente)
      );

      return {
        esCliente: true,
        tipoProyecto: null,
        proyectoId: null,
        // Si no se encuentra proyecto, se le da un permiso mínimo para 'honorarios-mo'
        permisos: { vistas_permitidas: ["honorarios-mo"] },
        error: "No se encontró proyecto asociado a este email",
      };
    }

    console.log("✅ Proyecto encontrado:", proyecto);

    // Determinar permisos según tipo de obra
    const permisosCalculados = calcularPermisos(proyecto.tipo_obra);

    const resultado = {
      esCliente: true,
      tipoProyecto: proyecto.tipo_obra,
      proyectoId: proyecto.id,
      proyectoCodigo: proyecto.codigo,
      proyectoCliente: proyecto.apellido_cliente,
      proyectoFinalizado: proyecto.obra_finalizada,
      permisos: permisosCalculados, // Ahora esto contendrá { vistas_permitidas: [...] }
      error: null,
    };

    console.log("✅ Permisos calculados:", resultado);
    return resultado;
  } catch (err) {
    console.error("💥 Error obteniendo permisos cliente:", err);
    return {
      esCliente: true,
      tipoProyecto: null,
      proyectoId: null,
      permisos: {},
      error: "Error al verificar permisos: " + err.message,
    };
  }
};

/**
 * Calcula los permisos según el tipo de obra y devuelve las vistas permitidas.
 * @param {string} tipoObra - 'llave_en_mano' | 'administracion'
 * @returns {Object} - Objeto con las vistas_permitidas y otros permisos booleanos
 */
const calcularPermisos = (tipoObra) => {
  let vistas_permitidas = [];
  let permisosBooleanos = {
    // Vistas disponibles
    puedeVerHonorarios: true, // Siempre pueden ver honorarios
    puedeVerDashboard: false,
    puedeVerEntradas: false,
    puedeVerSalidas: false,
    puedeVerPresupuestos: false,
    puedeVerPresupuestoInicial: false, // <-- NUEVO: Permiso para Presupuesto Inicial
    puedeVerCambios: false,
    puedeVerGantt: false,
    puedeVerKPITrabajadores: false, // ✅ NUEVO: KPI Trabajadores (siempre false para clientes)
    // Acciones (siempre false para clientes)
    puedeEditar: false,
    puedeEliminar: false,
    puedeCrear: false,
    // Filtros de datos
    soloSuProyecto: true, // Siempre solo su proyecto
    soloOrigenCliente: true, // Solo datos donde origen sea cliente
    excluirManoObra: true, // Nunca ver datos de mano de obra
  };

  // Vistas siempre permitidas para todos los clientes (ej. Honorarios/Resumen)
  vistas_permitidas.push("honorarios-mo");

  if (tipoObra === "administracion") {
    vistas_permitidas.push(
      "dashboard",
      "entradas",
      "salidas",
      "presupuestos",
      "presupuesto-inicial", // <-- AÑADIDO: Presupuesto Inicial para Administración
      "cambios",
      "gantt",
      "cajas" // <-- Habilitar Cajas para clientes
    );
    Object.assign(permisosBooleanos, {
      puedeVerDashboard: true,
      puedeVerEntradas: true,
      puedeVerSalidas: true,
      puedeVerPresupuestos: true,
      puedeVerPresupuestoInicial: true, // <-- NUEVO: Setear a true para administración
      puedeVerCambios: true,
      puedeVerGantt: true,
      // KPI Trabajadores sigue siendo false para clientes
    });
  } else if (tipoObra === "llave_en_mano") {
    // Para 'llave_en_mano' además de honorarios, permitir Cajas
    if (!vistas_permitidas.includes("cajas")) {
      vistas_permitidas.push("cajas");
    }
  }

  // Se añade 'facturas-comprobantes' si puede ver entradas o salidas
  // Esto asegura que la vista Facturas/Comprobantes (que agrupa entradas y salidas) esté disponible
  if (permisosBooleanos.puedeVerEntradas || permisosBooleanos.puedeVerSalidas) {
    if (!vistas_permitidas.includes("facturas-comprobantes")) {
      vistas_permitidas.push("facturas-comprobantes");
    }
  }

  // No añadir 'facturacion' para clientes

  // ✅ IMPORTANTE: KPI Trabajadores NUNCA se añade a vistas_permitidas para clientes
  // Esta vista es exclusiva para empleados y socios
  
  // ✅ IMPORTANTE: Notas del Proyecto NUNCA se añade a vistas_permitidas para clientes
  // Esta vista es exclusiva para empleados y socios

  return {
    vistas_permitidas,
    ...permisosBooleanos, // Mantener los permisos booleanos para otras lógicas si se necesitan
  };
};

/**
 * Verifica si un cliente puede acceder a una vista específica
 * Esta función es usada por Sidebar.js
 * @param {Object} permisos - Permisos del cliente (el objeto completo 'permisos' del resultado de obtenerPermisosCliente, que incluye vistas_permitidas)
 * @param {string} vista - Nombre de la vista
 * @returns {boolean}
 */
export const puedeAccederAVista = (permisos, vista) => {
  // ✅ CONTROL ESPECIAL: KPI Trabajadores está prohibido para todos los clientes
  if (vista === "kpi-trabajadores") {
    return false; // Los clientes NUNCA pueden acceder a KPI Trabajadores
  }

  // ✅ CONTROL ESPECIAL: Notas del Proyecto está prohibido para todos los clientes
  if (vista === "notas-proyecto") {
    return false; // Los clientes NUNCA pueden acceder a Notas del Proyecto
  }

  // ✅ CONTROL ESPECIAL: Vistas de sistema (solo super admins)
  if (vista.startsWith("system-")) {
    return false; // Los clientes NUNCA pueden acceder a vistas de sistema
  }

  // Asegurarse de que permisos y vistas_permitidas existan
  if (!permisos || !permisos.vistas_permitidas) {
    // Si no hay permisos o no hay vistas permitidas, denegar acceso (o permitir solo 'honorarios-mo' como un fallback seguro)
    // La lógica de Sidebar.js ya tiene un manejo para `permisosClienteObj` null/undefined.
    // Esta función asume que el objeto `permisos` pasado es el que contiene `vistas_permitidas`.
    return vista === "honorarios-mo"; // Fallback muy básico
  }

  return permisos.vistas_permitidas.includes(vista);
};

/**
 * ✅ NUEVA FUNCIÓN: Verifica si un usuario puede acceder a KPI Trabajadores
 * Esta función verifica específicamente los permisos para la vista KPI Trabajadores
 * @param {Object} usuario - Objeto usuario con tipo_usuario
 * @returns {boolean}
 */
export const puedeVerKPITrabajadores = (usuario) => {
  if (!usuario || !usuario.tipo_usuario) {
    return false;
  }

  // Solo empleados y socios pueden ver KPI Trabajadores
  return (
    usuario.tipo_usuario === "empleado" ||
    usuario.tipo_usuario === "constructora"
  );
};

/**
 * Genera filtros SQL para las consultas de clientes
 * @param {Object} datosCliente - Datos del cliente obtenidos de obtenerPermisosCliente
 * @returns {Object} - Filtros para aplicar en consultas
 */
export const generarFiltrosCliente = (datosCliente) => {
  if (!datosCliente.esCliente || !datosCliente.proyectoId) {
    return {
      debeAplicarFiltros: false,
      filtros: {},
    };
  }

  return {
    debeAplicarFiltros: true,
    filtros: {
      proyecto_id: datosCliente.proyectoId,
      // Los permisos booleanos (soloOrigenCliente, excluirManoObra) se siguen usando desde aquí
      soloOrigenCliente: datosCliente.permisos.soloOrigenCliente,
      excluirManoObra: datosCliente.permisos.excluirManoObra,
    },
  };
};

/**
 * Filtrar entradas para clientes (origen cliente O cajas de proyecto, NO mano_obra)
 * @param {Array} entradas - Array de entradas
 * @param {Object} datosCliente - Datos del cliente
 * @param {Array} cajas - Array de cajas para verificar tipos
 * @returns {Array} - Entradas filtradas
 */
export const filtrarEntradasParaCliente = (
  entradas,
  datosCliente,
  cajas = []
) => {
  // Asumiendo que datosCliente.permisos ya contiene los flags booleanos
  if (!datosCliente.esCliente || !datosCliente.permisos.puedeVerEntradas) {
    return [];
  }

  return entradas.filter((entrada) => {
    // Solo entradas de su proyecto
    if (entrada.proyecto_id !== datosCliente.proyectoId) return false;

    // Si no tiene caja origen, es pago directo del cliente (PERMITIDO)
    if (!entrada.caja_origen_id) return true;

    // Si tiene caja origen, verificar que NO sea mano_obra
    const cajaOrigen = cajas?.find((c) => c.id === entrada.caja_origen_id);
    if (cajaOrigen && cajaOrigen.tipo_caja === "mano_obra") return false;

    // Permitir entradas de cajas empresa y proyecto
    return true;
  });
};

/**
 * Filtrar salidas para clientes (solo origen cliente O cajas de proyecto, NO mano_obra)
 * @param {Array} salidas - Array de salidas
 * @param {Object} datosCliente - Datos del cliente
 * @param {Array} cajas - Array de cajas para verificar tipos
 * @returns {Array} - Salidas filtradas
 */
export const filtrarSalidasParaCliente = (
  salidas,
  datosCliente,
  cajas = []
) => {
  console.log("🔍 FILTRANDO SALIDAS PARA CLIENTE");
  console.log("📊 Salidas recibidas:", salidas.length);
  console.log("👤 Datos cliente:", datosCliente);
  console.log("🏦 Cajas:", cajas.length);

  if (!datosCliente.esCliente || !datosCliente.permisos.puedeVerSalidas) {
    console.log("❌ Cliente no puede ver salidas");
    return [];
  }

  const salidasFiltradas = salidas.filter((salida) => {
    console.log(
      `🔍 Evaluando salida proyecto_id: ${salida.proyecto_id} vs cliente proyectoId: ${datosCliente.proyectoId}`
    );

    // Solo salidas de su proyecto
    if (salida.proyecto_id !== datosCliente.proyectoId) {
      console.log(`❌ Salida rechazada - proyecto diferente`);
      return false;
    }

    // Si no tiene caja origen, es pago directo del cliente (PERMITIDO)
    if (!salida.caja_origen_id) {
      console.log(`✅ Salida aceptada - pago directo del cliente`);
      return true;
    }

    // Si tiene caja origen, verificar que NO sea mano_obra
    // Primero intentar desde los datos de la salida (query)
    let cajaOrigen = salida.cajas; // Datos desde el query SQL
    console.log("🏦 Caja desde query:", cajaOrigen);

    // Si no viene en el query, buscar en el array de cajas
    if (!cajaOrigen) {
      cajaOrigen = cajas?.find((c) => c.id === salida.caja_origen_id);
      console.log("🏦 Caja desde array:", cajaOrigen);
    }

    if (cajaOrigen && cajaOrigen.tipo_caja === "mano_obra") {
      console.log(
        `❌ Salida rechazada - caja mano de obra: ${cajaOrigen.nombre}`
      );
      return false;
    }

    console.log(`✅ Salida aceptada - caja válida`);
    // Permitir salidas de cajas empresa y proyecto
    return true;
  });

  console.log(
    "📊 RESULTADO FILTRO SALIDAS:",
    salidasFiltradas.length,
    "salidas válidas"
  );
  return salidasFiltradas;
};

/**
 * Filtrar cobros para clientes (solo origen proyecto/empresa, NO mano_obra)
 * @param {Array} cobros - Array de cobros
 * @param {Object} datosCliente - Datos del cliente
 * @param {Array} cajas - Array de cajas para verificar tipos
 * @returns {Array} - Cobros filtrados
 */
export const filtrarCobrosParaCliente = (cobros, datosCliente, cajas = []) => {
  console.log("🔍 FILTRANDO COBROS PARA CLIENTE");
  console.log("📊 Cobros recibidos:", cobros.length);
  console.log("👤 Datos cliente:", datosCliente);
  console.log("🏦 Cajas:", cajas.length);

  if (!datosCliente.esCliente) {
    // La vista de cobros no debería mostrarse si el cliente no es cliente (obvio)
    console.log("❌ Cliente no puede ver cobros - no es cliente");
    return [];
  }
  // También podríamos verificar si permisos.puedeVerCobros si se quiere añadir esa lógica
  // if (!datosCliente.permisos.puedeVerCobros) { ... }

  const cobrosFiltrados = cobros.filter((cobro) => {
    console.log(
      `🔍 Evaluando cobro trabajador_id: ${cobro.trabajador_id}, caja_origen_id: ${cobro.caja_origen_id}`
    );

    // Si no tiene caja origen, es pago directo (NO PERMITIDO para cobros - siempre deben tener caja)
    if (!cobro.caja_origen_id) {
      console.log(`❌ Cobro rechazado - sin caja origen`);
      return false;
    }

    // Primero intentar desde los datos del cobro (query)
    let cajaOrigen = cobro.cajas; // Datos desde el query SQL
    console.log("🏦 Caja desde query:", cajaOrigen);

    // Si no viene en el query, buscar en el array de cajas
    if (!cajaOrigen) {
      cajaOrigen = cajas?.find((c) => c.id === cobro.caja_origen_id);
      console.log("🏦 Caja desde array:", cajaOrigen);
    }

    // Verificar que NO sea caja de mano_obra
    if (cajaOrigen && cajaOrigen.tipo_caja === "mano_obra") {
      console.log(
        `❌ Cobro rechazado - caja mano de obra: ${cajaOrigen.nombre}`
      );
      return false;
    }

    console.log(
      `✅ Cobro aceptado - caja válida: ${cajaOrigen?.nombre || "N/A"}`
    );
    // Permitir cobros de cajas empresa y proyecto
    return true;
  });

  console.log(
    "📊 RESULTADO FILTRO COBROS:",
    cobrosFiltrados.length,
    "cobros válidos"
  );
  return cobrosFiltrados;
};

/**
 * Filtrar cambios para clientes (solo origen proyecto, excluir mano_obra)
 * @param {Array} cambios - Array de cambios
 * @param {Object} datosCliente - Datos del cliente
 * @param {Array} cajas - Array de cajas para verificar tipos
 * @returns {Array} - Cambios filtrados
 */
export const filtrarCambiosParaCliente = (cambios, datosCliente, cajas) => {
  if (!datosCliente.esCliente || !datosCliente.permisos.puedeVerCambios) {
    return [];
  }

  return cambios.filter((cambio) => {
    // Solo cambios de su proyecto
    if (cambio.proyecto_id !== datosCliente.proyectoId) return false;

    // Verificar que la caja origen NO sea mano_obra
    const cajaOrigen = cajas.find((c) => c.id === cambio.caja_origen_id);
    if (cajaOrigen && cajaOrigen.tipo_caja === "mano_obra") return false;

    return true;
  });
};

/**
 * Obtener mensaje de restricción para mostrar al cliente
 * @param {Object} datosCliente - Datos del cliente
 * @param {string} vista - Vista actual
 * @returns {string|null} - Mensaje de restricción o null si puede acceder
 */
export const obtenerMensajeRestriccion = (datosCliente, vista) => {
  if (!datosCliente.esCliente) return null;

  // ✅ CONTROL ESPECIAL para KPI Trabajadores
  if (vista === "kpi-trabajadores") {
    return "La vista KPI Trabajadores es exclusiva para empleados y socios de la constructora. Esta sección contiene información interna de rendimiento del equipo.";
  }

  // ✅ CONTROL ESPECIAL para vistas de sistema
  if (vista.startsWith("system-")) {
    return "Esta sección es exclusiva para administradores del sistema NOKK.";
  }

  // Ahora, datosCliente.permisos ya contiene el array vistas_permitidas
  if (
    !datosCliente.permisos ||
    !datosCliente.permisos.vistas_permitidas.includes(vista)
  ) {
    if (datosCliente.tipoProyecto === "llave_en_mano") {
      return `Los clientes de obras Llave en Mano solo pueden acceder a la vista de Honorarios y Mano de Obra. Para consultas adicionales, contacta a ${
        datosCliente.constructora?.nombre || "tu constructora"
      }.`;
    } else {
      return `No tienes permisos para acceder a esta sección. Contacta a ${
        datosCliente.constructora?.nombre || "tu constructora"
      } si necesitas más información.`;
    }
  }

  return null;
};
